import { type ConfirmationType } from '@/entities/confirmationType/@/tour';
import {
  type HotelProduct,
  type HotelCategoryData,
  type MealData,
  OperatorProduct,
  type Product,
  type ProductDictionaries,
} from '@/entities/product/@/tour';
import { ProductType } from '@/shared/config/pages';
import {
  type TourProductDTO,
  type DepartureCity,
  type Country,
  type Resort,
  type RoomGroupDTO,
  type FirstPaymentDefinitionsDTO,
} from '@/shared/model';
import type { FlightType } from './types';
import { getFlightType } from '../lib/getFlightType';
import { getTourConfirmationType } from '../lib/getTourConfirmationType';

export default class TourProduct implements Product {
  readonly type = ProductType.TourOffer;
  id: string;
  price: number;
  oilTax: number | null;
  maxHistoricalPrice: number;
  discount: number;
  sortRate: number;
  checkInDate: string;
  departureCity: DepartureCity | null;
  country: Country | null;
  resort: Resort | null;
  hotel: HotelProduct | null;
  hotelCategory: HotelCategoryData | null;
  hotelAvailable: number;
  flightType: FlightType;
  confirmationType: ConfirmationType | null;
  block: boolean;
  nights: { total: number; hotel: number };
  meal: MealData | null;
  operator: OperatorProduct | null;
  roomName: string;
  roomGroups: RoomGroupDTO[];
  cashbackAvailable: boolean;
  medicalInsurance: boolean;
  firstPaymentDefinitions: FirstPaymentDefinitionsDTO[];

  constructor(data: TourProductDTO, dictionaries: ProductDictionaries) {
    const { countries, departureCities, meals, hotelCategories, firstPaymentDefinitions, resorts, hotels, operators } =
      dictionaries;
    this.id = data.id;
    this.checkInDate = data.checkInDate;
    this.price = data.price + (data.oilTax || 0);
    this.oilTax = data.oilTax ?? null;
    this.country = countries[data.country] || null;
    this.meal = meals[data.meal] || null;
    this.hotel = hotels[data.hotel] || null;
    this.departureCity = departureCities[data.departureCity] || null;
    this.firstPaymentDefinitions = data.firstPaymentDefinitions
      .map((id) => firstPaymentDefinitions[id])
      .filter(Boolean);
    this.resort = resorts[data.resort] || null;
    this.medicalInsurance = data.medicalInsurance;
    this.maxHistoricalPrice = data.maxHistoricalPrice;
    this.discount = data.discount;
    this.sortRate = data.sortRate;
    this.hotelCategory = hotelCategories[data.hotelCategory] || null;
    this.hotelAvailable = data.hotelAvailable;
    this.flightType = getFlightType(data.flightType);
    this.nights = { total: data.nights.tour, hotel: data.nights.hotel };
    this.operator = operators[data.operator] ? new OperatorProduct(operators[data.operator]) : null;
    this.roomName = typeof data.room === 'string' ? data.room : data.room?.name ?? '';
    this.roomGroups = data.roomGroups || [{ touristGroup: data.touristGroup, hash: '', room: '' }];
    this.cashbackAvailable = data.cashbackAvailable;
    this.block = !!data.chairTypeBlock;
    this.confirmationType = getTourConfirmationType({
      hotelAvailable: this.hotelAvailable,
      operatorId: this.operator?.id ?? null,
      flightType: this.flightType,
    });
  }
}
